/**
 * Transform utility for CPSC product identifiers.
 *
 * Schema format:
 *   itemIdentifiers: [{type: SE|MN|RN|ALT, number: string}]
 *   productCodes: [{type: SKU, code: string}]
 *
 * Form format (unified):
 *   productIdentifiers: [{type: SE|MN|RN|ALT|SKU, value: string}]
 *
 * This allows the UI to use a single repeatable section for all product
 * identifiers while maintaining compatibility with the structured GraphQL schema.
 */
import type {
  CpscItem,
  CpscItemInput,
  ItemIdentifierType,
  ProductCodeType,
} from '__generated__/graphql';
import {
  ItemIdentifierType as ItemIdentifierTypeEnum,
  ProductCodeType as ProductCodeTypeEnum,
} from '__generated__/graphql';

/**
 * Unified form state type for product identifiers.
 * Used by ProductIdentifierFields component.
 */
export interface ProductIdentifierFormState {
  type?: ItemIdentifierType | ProductCodeType;
  value?: string;
}

/** Item identifier types (stored in itemIdentifiers array) */
const ITEM_IDENTIFIER_TYPES: string[] = [
  ItemIdentifierTypeEnum.SE,
  ItemIdentifierTypeEnum.MN,
  ItemIdentifierTypeEnum.RN,
  ItemIdentifierTypeEnum.ALT,
];

/** Product code types (stored in productCodes array) */
const PRODUCT_CODE_TYPES: string[] = [ProductCodeTypeEnum.SKU];

export const transformProductIdentifiers = {
  /**
   * Transform GraphQL schema → Form state
   * Combines itemIdentifiers[] and productCodes[] into unified productIdentifiers[]
   *
   * @param {CpscItem} item - CpscItem with separate identifier/code arrays
   * @return {ProductIdentifierFormState[]} Unified array for UI form
   */
  toForm: (item?: CpscItem): ProductIdentifierFormState[] | undefined => {
    if (!item) return undefined;

    const identifiers: ProductIdentifierFormState[] = [];

    // Transform itemIdentifiers (SE, MN, RN, ALT)
    if (item.itemIdentifiers?.length) {
      item.itemIdentifiers.forEach((id) => {
        if (id) {
          identifiers.push({ type: id.type, value: id.number });
        }
      });
    }

    // Transform productCodes (SKU)
    if (item.productCodes?.length) {
      item.productCodes.forEach((pc) => {
        if (pc) {
          identifiers.push({ type: pc.type, value: pc.code });
        }
      });
    }

    return identifiers.length > 0 ? identifiers : undefined;
  },

  /**
   * Transform Form state → GraphQL schema
   * Splits unified productIdentifiers[] back into itemIdentifiers[] and productCodes[]
   *
   * @param {ProductIdentifierFormState[]} identifiers - Unified array from UI form
   * @return {Partial<CpscItemInput>} CpscItem with separate identifier/code arrays
   */
  toSchema: (
    identifiers?: ProductIdentifierFormState[],
  ): Partial<CpscItemInput> => {
    if (!identifiers?.length) {
      return {};
    }

    const result: Partial<CpscItemInput> = {};

    // Filter and transform item identifiers (SE, MN, RN, ALT)
    const itemIdentifiers = identifiers
      .filter((id) => id.type && ITEM_IDENTIFIER_TYPES.includes(id.type))
      .map((id) => ({
        type: id.type as ItemIdentifierType,
        number: id.value || '',
      }));

    if (itemIdentifiers.length) {
      result.itemIdentifiers = itemIdentifiers;
    }

    // Filter and transform product codes (SKU)
    const productCodes = identifiers
      .filter((id) => id.type && PRODUCT_CODE_TYPES.includes(id.type))
      .map((id) => ({
        type: id.type as ProductCodeType,
        code: id.value || '',
      }));

    if (productCodes.length) {
      result.productCodes = productCodes;
    }

    return result;
  },
};

export default transformProductIdentifiers;
